/**
 * Component-free widget specification map.
 *
 * This module holds the *platform-neutral* widget trunk: the `WidgetConfig`
 * shape, the deterministic id back-fill (`preprocessWidgetData` + the per-widget
 * transforms), and the `createWidgetSpecMap` factory that binds a platform
 * `componentMap` to each widget's data schema. The `satisfies` check forces
 * every platform to cover all `WidgetType`s.
 *
 * It deliberately imports no DOM/React (or Sentry) code, so the shared data
 * engine and a future React Native UI can consume widget validation/dispatch
 * logic without pulling web-only dependencies. The per-platform widget
 * *components* are supplied via the `componentMap`; widget *data* validation
 * lives in `widget-schemas.ts` (`validateWidgetData`, `normalizeWidgetName`).
 */
import type z from 'zod';

import type { WidgetData, WidgetType } from './widget-schemas.js';

import { generateFlightOptionId, generateImageId, generatePointId } from './widget-ids.js';
import {
  FlightSearchWidgetDataSchema,
  ImageGalleryWidgetDataSchema,
  ImageSearchWidgetDataSchema,
  MapWidgetDataSchema,
  PreferenceProposalWidgetDataSchema,
  RecommendationWidgetDataSchema,
  RoomSearchWidgetDataSchema,
} from './widget-schemas.js';

export type Dict = Record<string, any>;

/**
 * Generic widget configuration: a platform `component` plus a pure props
 * transform. `Node` is the platform's render output (e.g. `React.ReactNode` on
 * web); it is left generic here so the domain package stays DOM/React-free.
 */
export interface WidgetConfig<Data extends Dict, ReactProps extends Dict, Node = unknown> {
  component: (props: ReactProps) => Node;
  makeReactProps: (widgetData: Data) => ReactProps;
}

export type AnyWidgetConfig<Node = unknown> = WidgetConfig<any, any, Node>;

export function createWidgetConfig<DataSchema extends z.ZodTypeAny, ReactProps extends Dict, Node = unknown>(
  schema: DataSchema,
  component: (props: ReactProps) => Node,
  transformProps?: (widgetData: Extract<z.output<DataSchema>, Dict>) => ReactProps,
): WidgetConfig<Extract<z.output<DataSchema>, Dict>, ReactProps, Node> {
  void schema; // schema is used only for type inference of the transform input
  return {
    component,
    makeReactProps: transformProps ?? ((x: Extract<z.output<DataSchema>, Dict>) => x as unknown as ReactProps),
  };
}

/** Platform-provided components, one per widget type. `Node` is the platform render output. */
export type WidgetComponentMap<Node = unknown> = {
  [K in WidgetType]: (props: any) => Node;
};

/** The fully-bound widget spec map produced by `createWidgetSpecMap`. */
export type WidgetSpecMap<Node = unknown> = {
  [K in WidgetType]: WidgetConfig<WidgetData<K>, any, Node>;
};

const DEFAULT_FLIGHT_PASSENGERS = { adults: 1, children: 0, infants: 0 };

/**
 * Binds a platform `componentMap` to the shared widget data schemas and id
 * back-fill transforms. The `satisfies WidgetSpecMap<Node>` check is the
 * single source of exhaustiveness: every `WidgetType` must have a component,
 * and each config's data type must match its schema.
 */
export function createWidgetSpecMap<Node>(components: WidgetComponentMap<Node>) {
  return {
    showImagesByIdsWidget: createWidgetConfig(ImageSearchWidgetDataSchema, components.showImagesByIdsWidget),
    showRoomResultsWidget: createWidgetConfig(RoomSearchWidgetDataSchema, components.showRoomResultsWidget),
    showImageGalleryWidget: createWidgetConfig(ImageGalleryWidgetDataSchema, components.showImageGalleryWidget, (props) => {
      const images = (props.images || []).map((image, idx) => {
        const id = image.id ?? generateImageId(image, idx);
        return { ...image, id };
      });
      return { ...props, images };
    }),
    showMapWidget: createWidgetConfig(MapWidgetDataSchema, components.showMapWidget, (props) => {
      const points = (props.points || []).map((point, idx) => {
        const id = point.id ?? generatePointId(point, idx);
        return { ...point, id };
      });
      return { ...props, points };
    }),
    showRecommendationWidget: createWidgetConfig(
      RecommendationWidgetDataSchema,
      components.showRecommendationWidget,
      // Normalise the two id shapes into one array so a platform component never handles both, and
      // BACK-FILL the scalar from the array. The back-fill is what lets a multi-entity widget reach a
      // client that only knows `referenceId` — mobile's component reads the scalar and renders the
      // first entity rather than nothing at all, so the schema change needs no mobile release.
      // DE-DUPED as well, because nothing upstream guarantees uniqueness: the schema caps the array's
      // length but not its contents, and a model that repeated an id would hand both platforms two
      // children with the same React key — one card silently dropped, and a console warning as the
      // only sign. Cheaper to settle here, once, than to trust every present and future caller.
      (recommendation) => {
        const ids = [
          ...new Set(
            recommendation.referenceIds?.length
              ? recommendation.referenceIds
              : recommendation.referenceId
                ? [recommendation.referenceId]
                : [],
          ),
        ];
        return { ...recommendation, referenceId: recommendation.referenceId ?? ids[0], referenceIds: ids };
      },
    ),
    showPreferenceProposalWidget: createWidgetConfig(PreferenceProposalWidgetDataSchema, components.showPreferenceProposalWidget),
    showFlightResultsWidget: createWidgetConfig(
      FlightSearchWidgetDataSchema,
      components.showFlightResultsWidget,
      (flightSearchResults) => {
        const flightOptionsWithIds = (flightSearchResults.results || []).map((flightOption, idx) => {
          const id = flightOption.id ?? generateFlightOptionId(flightOption, idx);
          const passengers = flightOption.passengers ?? DEFAULT_FLIGHT_PASSENGERS;
          return { ...flightOption, id, passengers };
        });
        return { ...flightSearchResults, results: flightOptionsWithIds };
      },
    ),
  } satisfies WidgetSpecMap<Node>;
}

/**
 * Component-free widget spec map (ELARA-700).
 *
 * Consumers that only need the data side of a widget — its `makeReactProps`
 * transform / id back-fill, not the platform `component` — use this instead of a
 * web component registry. The transforms are identical regardless of platform, so
 * binding no-op components here keeps a single source for them while staying
 * DOM/React-free (e.g. `ToolCallModel` builds widget react-props off this).
 */
const NEUTRAL_WIDGET_COMPONENT = () => undefined;
export const neutralWidgetSpecMap: WidgetSpecMap = createWidgetSpecMap<unknown>({
  showImagesByIdsWidget: NEUTRAL_WIDGET_COMPONENT,
  showRoomResultsWidget: NEUTRAL_WIDGET_COMPONENT,
  showImageGalleryWidget: NEUTRAL_WIDGET_COMPONENT,
  showMapWidget: NEUTRAL_WIDGET_COMPONENT,
  showRecommendationWidget: NEUTRAL_WIDGET_COMPONENT,
  showPreferenceProposalWidget: NEUTRAL_WIDGET_COMPONENT,
  showFlightResultsWidget: NEUTRAL_WIDGET_COMPONENT,
});

/** Union type for all widget input props (the validated widget data). */
export type WidgetInputProps = WidgetData<WidgetType>;

/** Union type for all widget output (transformed) props across a spec map. */
export type WidgetReactProps<Map extends WidgetSpecMap<any> = WidgetSpecMap> = {
  [K in keyof Map]: Map[K] extends WidgetConfig<any, infer ReactProps, any> ? ReactProps : never;
}[keyof Map];

/**
 * Simple deterministic hash for generating stable ids from strings (typically URLs).
 * @returns Base36 hash string
 */
export function hashCode(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.codePointAt(i) ?? 0;
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Preprocesses widget data to ensure all required ids are present, generating
 * deterministic ids (URL hash or index) for items missing them. This handles
 * AI/external payloads that omit id fields so Zod validation passes before the
 * data reaches widget components.
 */
export function preprocessWidgetData<const Data>(data: Data): Data {
  if (!data || typeof data !== 'object') return data;
  if (Array.isArray(data)) return data;

  const dataObj = { ...(data as Record<string, unknown>) };

  // Normalize images array (used by ImageGallery and Recommendation)
  if ('images' in dataObj && Array.isArray(dataObj.images)) {
    dataObj.images = dataObj.images.map((img, index) => {
      if (img && typeof img === 'object' && !('id' in img)) {
        const imgObj = img as Record<string, unknown>;
        const url = typeof imgObj.url === 'string' ? imgObj.url : '';
        const id = url ? `img-${hashCode(url)}` : `img-${index}`;
        return { ...imgObj, id };
      }
      return img as { id: string; url: string };
    });
  }

  // Normalize points array (used by Map)
  if ('points' in dataObj && Array.isArray(dataObj.points)) {
    dataObj.points = dataObj.points.map((point: unknown, index: number) => {
      if (point && typeof point === 'object' && !('id' in point)) {
        return { ...point, id: `point-${index}` };
      }
      return point;
    });
  }

  return dataObj as Data;
}
